import {
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
  scrollTo as scrollPageTo,
} from '@theme/scroll-container';

const REVEAL_THRESHOLD = 300;

class BackToTop extends HTMLElement {
  /** @type {HTMLAnchorElement | null} */
  #button = null;

  /** @type {EventTarget | null} */
  #scrollTarget = null;

  /** @type {number | null} */
  #animationFrame = null;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#button = this.querySelector('.back-to-top__button');
    if (!this.#button) return;

    this.#button.addEventListener('click', this.#handleClick);
    scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    this.#bindScrollTarget();
    this.#syncVisibility();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);

    if (this.#animationFrame !== null) cancelAnimationFrame(this.#animationFrame);
  }

  #bindScrollTarget() {
    this.#scrollTarget?.removeEventListener('scroll', this.#handleScroll);
    this.#scrollTarget = getScrollEventTarget();
    this.#scrollTarget.addEventListener('scroll', this.#handleScroll, { passive: true });
  }

  #handleBreakpointChange = () => {
    this.#bindScrollTarget();
    this.#syncVisibility();
  };

  #handleScroll = () => {
    if (this.#animationFrame !== null) return;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = null;
      this.#syncVisibility();
    });
  };

  #syncVisibility() {
    if (!this.#button) return;

    const isVisible = getScrollTop() > REVEAL_THRESHOLD;
    this.toggleAttribute('data-visible', isVisible);
    this.#button.tabIndex = isVisible ? 0 : -1;
    this.#button.setAttribute('aria-hidden', `${!isVisible}`);
  }

  #handleClick = (event) => {
    event.preventDefault();

    const pageTop = document.getElementById('PageTop');
    pageTop?.focus({ preventScroll: true });

    scrollPageTo({
      top: 0,
      left: 0,
      behavior: this.#reducedMotion.matches ? 'auto' : 'smooth',
    });
  };
}

if (!customElements.get('back-to-top')) {
  customElements.define('back-to-top', BackToTop);
}
